import {
    Button,
    Checkbox,
    Label,
    makeStyles,
    OptionOnSelectData,
    SearchBox,
    SelectionEvents,
    shorthands,
    Spinner,
    tokens,
} from '@fluentui/react-components';
import { Warning20Regular } from '@fluentui/react-icons';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrintReportModal } from '../../contexts/printReportModalContext';
import { useDefaultModalStyles } from '../../hooks/useDefaultModalStyles';
import { IWhitePaperColumn, TOCSelectOption } from '../../model';
import { ENTRY_POINT_DIALOG_CLOSE } from '../../telemetry/entryPoints';
import { ACTION_INVOKED_EVENT, PRINT_REPORT_MODAL_CLOSED } from '../../telemetry/events';
import { useEventTracker } from '../../telemetry/hooks/useEventTracker';
import EntitySelectorCombobox from '../tables/common-individual-report-table/EntitySelectorCombobox';
import { DefaultTooltipHostV9 } from './DefaultTooltipHostV9';
import LoadingSpinner from './LoadingSpinner';
import Modal from './ModalV9';
import SelectableFormTOCTreeView from './SelectableFormTOCTreeView';

export interface PrintReportModalProps {
    reportName: string;
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (value: TOCSelectOption[] | undefined, doNotPrintBlankForms: boolean) => void;
    isSubmitting: boolean;
    entityDescription?: string;
}

const useStyles = makeStyles({
    spinnerContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    reportName: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
    },
    entityDescription: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightRegular,
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    bodyContentWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '12px',
    },
    bodyContent: {
        maxHeight: '500px',
        height: '500px',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        minHeight: 0,
        overflow: 'hidden',
    },
    infoContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    hiddenInfoContent: {
        visibility: 'hidden',
    },
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        minHeight: 0,
        gap: '8px',
    },
    selectContentSection: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        minHeight: 0,
    },
    selectContentContainer: {
        ...shorthands.border('1px', 'solid', tokens.colorNeutralBackground6),
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        minHeight: 0,
        overflowX: 'hidden',
    },
    searchBox: {
        width: '100%',
        minWidth: '100%',
        maxWidth: '100%',
    },
    printExportWarningMessageContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        textWrap: 'wrap',
    },
    printExportWarningIcon: {
        alignSelf: 'flex-start',
        marginTop: '2px',
    },
    bodyStyles: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        height: '100%',
        minHeight: 0,
    },
    contentStyles: {
        lineHeight: tokens.lineHeightBase300,
        overflowY: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
    },
    tree: {
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        minHeight: 0,
        scrollbarGutter: 'stable',
    },
    selectAllCheckbox: {
        // the select all checkbox needs to have the same spacing as the items in the tree, according to the design.
        // however, the tree does not render the select all checkbox.
        // it is rendered as a sibling of the tree.
        // spacing between those siblings is 12px.
        // so we can set negative margin to make up the difference for this one element
        // if we later learn that we need a multi-select tree with select all functionality in many other places,
        // we can look to consolidate those two things into the same component and get rid of this style
        marginBottom: '-4px',
        ':hover': {
            color: tokens.colorNeutralForeground2Hover,
            backgroundColor: tokens.colorSubtleBackgroundHover,
        },
    },
    doNotPrintBlankFormsCheckbox: {
        marginTop: '4px',
    },
    doNotPrintBlankFormsCheckboxText: {
        color: tokens.colorNeutralForeground3,
    },
});

const PrintReportModal: React.FC<PrintReportModalProps> = ({ reportName, isOpen, onClose, onSubmit, isSubmitting, entityDescription }) => {
    const { t } = useTranslation();
    const {
        isLoadingFormTOC,
        openNodeIds,
        checkedStateById,
        filteredTocData,
        selectedAndTotalItemsCount,
        hasFormsSelectedForSubmit,
        submitPayload,
        onSelectableCheckedStateChange,
        onSelectableOpenNodeIdsChange,
        onSearchChange,
        selectAllCheckboxState,
        onSelectAllChange,
        entities,
        selectedEntity,
        onEntitySelect,
        isEntitySelectorEnabled,
        isDoNotPrintBlankFormsAvailable,
        onDoNotPrintBlankFormsCheckedStateChange,
        doNotPrintBlankForms,
    } = usePrintReportModal();
    const { trackEvent } = useEventTracker();
    const { button } = useDefaultModalStyles();

    const styles = useStyles();
    const selectedEntityId: string[] = selectedEntity?.columnId ? [selectedEntity.columnId.toString()] : [];
    const selectedEntityDisplay: string = selectedEntity?.description ?? '';
    const { selectedCount, totalCount } = selectedAndTotalItemsCount;

    const handleEntitySelect = (_event: SelectionEvents, data: OptionOnSelectData): void => {
        const optionValue: string | undefined = data.optionValue;
        if (!optionValue) {
            onEntitySelect?.(undefined);
            return;
        }

        const nextEntity: IWhitePaperColumn | undefined = entities?.find(
            (column: IWhitePaperColumn) => column.columnId === parseInt(optionValue, 10)
        );
        onEntitySelect?.(nextEntity);
    };

    const handleClose = useCallback(() => {
        trackEvent(ACTION_INVOKED_EVENT, { action_name: PRINT_REPORT_MODAL_CLOSED, entry_point: ENTRY_POINT_DIALOG_CLOSE });
        onClose();
    }, [onClose, trackEvent]);

    const exportReportModalButtons = (
        <>
            <Button
                appearance='primary'
                disabled={Boolean(!hasFormsSelectedForSubmit || isLoadingFormTOC || isSubmitting)}
                size={'small'}
                data-testid='submit-export-print'
                aria-label={t('confirm').toString()}
                className={button}
                onClick={() => onSubmit(submitPayload, doNotPrintBlankForms)}
            >
                {isSubmitting ? t('working').toString() : t('confirm').toString()}
                {isSubmitting && <Spinner size='extra-tiny' />}
            </Button>
            <Button
                size={'small'}
                aria-label={t('cancel').toString()}
                onClick={handleClose}
                data-testid='close-export-print'
                className={button}
            >
                {t('cancel').toString()}
            </Button>
        </>
    );

    const selectedItemsText: string = `${selectedCount} of ${totalCount} ${t('items_selected').toString()}`;
    const shouldHideSelectedItemsText: boolean = isLoadingFormTOC;
    const infoContentClassName: string = shouldHideSelectedItemsText ? styles.hiddenInfoContent : '';
    const infoContentText: string = shouldHideSelectedItemsText ? '\u00A0' : selectedItemsText;
    const bodyContent = (
        <div className={styles.bodyContentWrapper}>
            <div className={styles.bodyContent}>
                <div className={styles.modalContent}>
                    {isEntitySelectorEnabled && (
                        <div>
                            <Label>{t('selectEntity')}</Label>
                            <EntitySelectorCombobox
                                reportColumns={entities!}
                                onOptionSelect={handleEntitySelect}
                                selectedOptions={selectedEntityId}
                                value={selectedEntityDisplay}
                                disabled={isLoadingFormTOC}
                            />
                        </div>
                    )}
                    <div className={styles.selectContentSection}>
                        {isEntitySelectorEnabled && <Label>{t('selectDocuments')}</Label>}
                        <div className={styles.selectContentContainer}>
                            {isLoadingFormTOC ? (
                                <div className={styles.spinnerContainer}>
                                    <LoadingSpinner label={t('loading').toString()} labelPosition='below' />
                                </div>
                            ) : (
                                <>
                                    <SearchBox
                                        className={styles.searchBox}
                                        placeholder={t('search').toString()}
                                        onChange={(_, data) => onSearchChange(data.value)}
                                    />
                                    {!isEntitySelectorEnabled && entityDescription && (
                                        <span tabIndex={0} className={styles.entityDescription}>
                                            {entityDescription}
                                        </span>
                                    )}
                                    {filteredTocData.length > 0 && (
                                        <Checkbox
                                            label={t('selectAll')}
                                            labelPosition='after'
                                            checked={selectAllCheckboxState}
                                            aria-checked={selectAllCheckboxState}
                                            onChange={onSelectAllChange}
                                            className={styles.selectAllCheckbox}
                                        />
                                    )}
                                    <SelectableFormTOCTreeView
                                        taxReturnItems={filteredTocData}
                                        noRecordsMessage={t('noRecordsToDisplay')}
                                        openNodeIds={openNodeIds}
                                        checkedStateById={checkedStateById}
                                        onOpenNodeIdsChange={onSelectableOpenNodeIdsChange}
                                        onCheckedStateChange={onSelectableCheckedStateChange}
                                        className={styles.tree}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.infoContent}>
                <div>
                    {isDoNotPrintBlankFormsAvailable && (
                        <DefaultTooltipHostV9
                            alwaysDisplay
                            appearance='inverted'
                            content={t('formsWithoutDataExcludedWhenChecked').toString()}
                            relationship='description'
                            withArrow
                        >
                            <Checkbox
                                checked={doNotPrintBlankForms}
                                className={styles.doNotPrintBlankFormsCheckbox}
                                label={{ className: styles.doNotPrintBlankFormsCheckboxText, children: t('dontPrintBlankForms') }}
                                onChange={(_, data) => onDoNotPrintBlankFormsCheckedStateChange(data.checked as boolean)}
                                tabIndex={0}
                            />
                        </DefaultTooltipHostV9>
                    )}
                </div>
                <div className={infoContentClassName} aria-hidden={shouldHideSelectedItemsText}>
                    <span>{infoContentText}</span>
                </div>
            </div>
        </div>
    );

    const headerContent = (
        <div className={styles.headerContent}>
            <span> {t('printDownloadPDF').toString()}</span>
            <span className={styles.reportName}>{reportName}</span>
        </div>
    );

    const printExportWarningMessage = (
        <div className={styles.printExportWarningMessageContainer} tabIndex={0}>
            <Warning20Regular className={styles.printExportWarningIcon} />
            <span>{t('proceedingPrintWarning')}</span>
        </div>
    );

    return (
        <>
            <Modal
                id='export-report-modal'
                headerContent={headerContent}
                bodyContent={bodyContent}
                contentStylesProps={styles.contentStyles}
                buttons={exportReportModalButtons}
                isOpen={isOpen}
                onClose={handleClose}
                leftFooterElement={printExportWarningMessage}
                bodyStylesProps={styles.bodyStyles}
            />
        </>
    );
};

export default PrintReportModal;
